import React, { useState } from "react";
import Layouts from "../../Layouts";
import { useHistory } from "react-router-dom";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import {
  Stethoscope,
  Bed,
  Scissors,
  Calendar,
  MoreVertical,
  ChevronDown,
  Clock,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  User,
} from "lucide-react";
import { FaAmbulance, FaProcedures } from "react-icons/fa";
import { Dropdown, Tooltip } from "antd";
import dayjs from "dayjs";

/* ─── Mock Datasets for Analytics ─── */
const OPD_APPOINTMENTS = [
  {
    id: "1",
    name: "Mr. Kumar S",
    patientId: "DZP5641807",
    time: "10:00 AM",
    reason: "Fever",
    status: "Waiting",
    statusType: "warning",
    avatarBg: "bg-teal-500",
    initial: "K",
  },
  {
    id: "2",
    name: "Ms. Priya R",
    patientId: "DZP1101815",
    time: "10:30 AM",
    reason: "Headache",
    status: "In Consultation",
    statusType: "info",
    avatarBg: "bg-cyan-500",
    initial: "P",
  },
  {
    id: "3",
    name: "Mr. Arun K",
    patientId: "DZP2401809",
    time: "11:00 AM",
    reason: "Diabetes Review",
    status: "Pending",
    statusType: "danger",
    avatarBg: "bg-amber-500",
    initial: "A",
  },
  {
    id: "4",
    name: "Mrs. Latha M",
    patientId: "DZP3301456",
    time: "11:30 AM",
    reason: "Cough & Cold",
    status: "Waiting",
    statusType: "warning",
    avatarBg: "bg-rose-400",
    initial: "L",
  },
  {
    id: "5",
    name: "Mr. Ramesh V",
    patientId: "DZP4401999",
    time: "12:00 PM",
    reason: "Hypertension",
    status: "Scheduled",
    statusType: "info",
    avatarBg: "bg-indigo-500",
    initial: "R",
  },
];

const OT_SCHEDULE = [
  {
    id: "1",
    time: "10:00 AM",
    procedure: "Appendectomy",
    otRoom: "OT-01",
    patient: "Mr. Suresh",
    status: "Scheduled",
    statusType: "info",
  },
  {
    id: "2",
    time: "12:30 PM",
    procedure: "Knee Replacement",
    otRoom: "OT-02",
    patient: "Mrs. Meena",
    status: "Scheduled",
    statusType: "info",
  },
  {
    id: "3",
    time: "03:00 PM",
    procedure: "Hernia Repair",
    otRoom: "OT-01",
    patient: "Mr. Rajesh",
    status: "Pending",
    statusType: "warning",
  },
  {
    id: "4",
    time: "05:30 PM",
    procedure: "Gall Bladder Surgery",
    otRoom: "OT-02",
    patient: "Mrs. Priya",
    status: "Pending",
    statusType: "warning",
  },
];

const ICU_PATIENTS = [
  {
    id: "1",
    patient: "Mr. Ravi K",
    bed: "ICU-01",
    condition: "Post Surgery",
    status: "Critical",
    statusType: "danger",
  },
  {
    id: "2",
    patient: "Mrs. Anitha",
    bed: "ICU-04",
    condition: "Pneumonia",
    status: "Stable",
    statusType: "success",
  },
  {
    id: "3",
    patient: "Mr. Kumar",
    bed: "ICU-08",
    condition: "Sepsis",
    status: "Stable",
    statusType: "success",
  },
  {
    id: "4",
    patient: "Mrs. Leela",
    bed: "ICU-10",
    condition: "Respiratory",
    status: "Critical",
    statusType: "danger",
  },
  {
    id: "5",
    patient: "Mr. Dinesh",
    bed: "ICU-12",
    condition: "Stroke",
    status: "Stable",
    statusType: "success",
  },
];

const EMERGENCY_ARRIVALS = [
  {
    id: "1",
    name: "Mr. Saravanan",
    patientId: "DZP5521330",
    arrivalTime: "09:15 AM",
    chiefComplaint: "Chest Pain",
    priority: "Critical",
    priorityType: "danger",
    status: "Under Review",
    statusType: "warning",
    avatarBg: "bg-purple-500",
    initial: "S",
  },
];

function Analytics() {
  const history = useHistory();
  const [selectedDateFilter, setSelectedDateFilter] = useState("Today, " + dayjs().format("DD MMM YYYY"));

  const storedLoginResult = sessionStorage.getItem("login_result");
  const loginResultObject = storedLoginResult ? JSON.parse(storedLoginResult) : null;
  const user = loginResultObject?.user;

  // Determine greeting based on current time
  const currentHour = new Date().getHours();
  const greeting =
    currentHour < 12 ? "Good Morning," : currentHour < 17 ? "Good Afternoon," : "Good Evening,";

  const dateFilterItems = [
    { key: "today", label: `Today, ${dayjs().format("DD MMM YYYY")}` },
    { key: "yesterday", label: `Yesterday, ${dayjs().subtract(1, "day").format("DD MMM YYYY")}` },
    { key: "last7", label: "Last 7 Days" },
    { key: "thisMonth", label: "This Month" },
  ];

  const handleDateSelect = ({ key }) => {
    const item = dateFilterItems.find((i) => i.key === key);
    if (item) setSelectedDateFilter(item.label);
  };

  const getStatusBadge = (status, type) => {
    switch (type) {
      case "danger":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-600 border border-red-200">
            {status}
          </span>
        );
      case "warning":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-600 border border-amber-200">
            {status}
          </span>
        );
      case "info":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-200">
            {status}
          </span>
        );
      case "success":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200">
            {status}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  const actionMenuItems = (item) => [
    {
      key: "view",
      label: "View Details",
      onClick: () => {},
    },
    {
      key: "consult",
      label: "Open Consultation",
      onClick: () => {},
    },
  ];

  return (
    <Layouts>
      <div className="mainContent w-[98%] mx-auto pb-10">
        {/* ─── Top Header: Doctor Info & Date Filter ─── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white border border-gray-100 rounded-xl p-3 md:p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#189FAA]/20 bg-cyan-50 flex items-center justify-center flex-shrink-0">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.firstName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-[#189FAA] text-white font-bold text-lg flex items-center justify-center">
                  {user?.firstName ? user.firstName.charAt(0).toUpperCase() : "D"}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium leading-tight">{greeting}</p>
              <h2 className="text-base md:text-lg font-sans font-bold text-[#041346] leading-snug">
                Dr. {user?.firstName || "Hariharan"} {user?.lastName || "V"}{" "}
                <span className="text-xs font-normal text-gray-600">
                  {user?.qualification || "MBBS(MD)"}
                </span>
              </h2>
              <p className="text-xs text-gray-500 font-medium">
                {user?.speciality || "General, Medicine"}
              </p>
            </div>
          </div>

          {/* Date Selector */}
          <div>
            <Dropdown
              menu={{ items: dateFilterItems, onClick: handleDateSelect }}
              trigger={["click"]}
            >
              <button className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#189FAA] px-3.5 py-1.5 rounded-lg text-xs md:text-sm font-semibold text-[#041346] shadow-2xs transition-colors">
                <Calendar className="w-4 h-4 text-[#189FAA]" />
                <span>{selectedDateFilter}</span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </Dropdown>
          </div>
        </div>

        {/* ─── Top 5 Module Stat Cards ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          {/* OPD Card */}
          <div
            onClick={() => history.push(APP_ROUTES.DASHBOARD)}
            className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between"
          >
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-xl bg-[#E0F7FA] flex items-center justify-center text-[#00897B] flex-shrink-0">
                <Stethoscope className="w-5 h-5" />
              </div>
              {/* Mini Bar Chart */}
              <div className="flex items-end gap-1 h-6">
                <div className="w-1.5 h-2 bg-[#80CBC4] rounded-full" />
                <div className="w-1.5 h-3.5 bg-[#4DB6AC] rounded-full" />
                <div className="w-1.5 h-5 bg-[#26A69A] rounded-full" />
                <div className="w-1.5 h-6 bg-[#00897B] rounded-full" />
              </div>
            </div>
            <div className="my-2">
              <p className="text-xs font-bold text-gray-800">OPD</p>
              <p className="text-[11px] text-gray-400 font-medium">Today's Patients</p>
              <p className="text-2xl font-extrabold text-[#041346] mt-0.5">12</p>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span>Pending</span>
                <span className="font-bold text-gray-800">3</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>Completed</span>
                <span className="font-bold text-gray-800">9</span>
              </div>
            </div>
          </div>

          {/* Emergency Card */}
          <div
            onClick={() => history.push(APP_ROUTES.EMERGENCY)}
            className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between"
          >
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-xl bg-[#FEE2E2] flex items-center justify-center text-[#EF4444] flex-shrink-0">
                <FaAmbulance className="w-5 h-5" />
              </div>
              <div className="flex items-end gap-1 h-6">
                <div className="w-1.5 h-2 bg-[#FCA5A5] rounded-full" />
                <div className="w-1.5 h-3.5 bg-[#F87171] rounded-full" />
                <div className="w-1.5 h-5 bg-[#EF4444] rounded-full" />
                <div className="w-1.5 h-6 bg-[#DC2626] rounded-full" />
              </div>
            </div>
            <div className="my-2">
              <p className="text-xs font-bold text-gray-800">Emergency</p>
              <p className="text-[11px] text-gray-400 font-medium">Active Cases</p>
              <p className="text-2xl font-extrabold text-[#041346] mt-0.5">03</p>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span>Critical</span>
                <span className="font-bold text-gray-800">1</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span>Urgent</span>
                <span className="font-bold text-gray-800">2</span>
              </div>
            </div>
          </div>

          {/* ICU Card */}
          <div
            onClick={() => history.push(APP_ROUTES.ICUWARDS)}
            className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between"
          >
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-xl bg-[#E0F2FE] flex items-center justify-center text-[#0284C7] flex-shrink-0">
                <Bed className="w-5 h-5" />
              </div>
              <div className="flex items-end gap-1 h-6">
                <div className="w-1.5 h-2 bg-[#BAE6FD] rounded-full" />
                <div className="w-1.5 h-3.5 bg-[#7DD3FC] rounded-full" />
                <div className="w-1.5 h-5 bg-[#38BDF8] rounded-full" />
                <div className="w-1.5 h-6 bg-[#0284C7] rounded-full" />
              </div>
            </div>
            <div className="my-2">
              <p className="text-xs font-bold text-gray-800">ICU</p>
              <p className="text-[11px] text-gray-400 font-medium">Assigned Patients</p>
              <p className="text-2xl font-extrabold text-[#041346] mt-0.5">05</p>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span>Critical</span>
                <span className="font-bold text-gray-800">2</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>Stable</span>
                <span className="font-bold text-gray-800">3</span>
              </div>
            </div>
          </div>

          {/* OT Card */}
          <div
            onClick={() => history.push(APP_ROUTES.OPERATIONTHEATRE)}
            className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between"
          >
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-xl bg-[#F3E8FF] flex items-center justify-center text-[#8B5CF6] flex-shrink-0">
                <Scissors className="w-5 h-5" />
              </div>
              <div className="flex items-end gap-1 h-6">
                <div className="w-1.5 h-2 bg-[#DDD6FE] rounded-full" />
                <div className="w-1.5 h-3.5 bg-[#C4B5FD] rounded-full" />
                <div className="w-1.5 h-5 bg-[#A78BFA] rounded-full" />
                <div className="w-1.5 h-6 bg-[#8B5CF6] rounded-full" />
              </div>
            </div>
            <div className="my-2">
              <p className="text-xs font-bold text-gray-800">OT</p>
              <p className="text-[11px] text-gray-400 font-medium">Today's Surgeries</p>
              <p className="text-2xl font-extrabold text-[#041346] mt-0.5">02</p>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-purple-500" />
                <span>Scheduled</span>
                <span className="font-bold text-gray-800">1</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>Completed</span>
                <span className="font-bold text-gray-800">1</span>
              </div>
            </div>
          </div>

          {/* Wards Card */}
          <div
            onClick={() => history.push(APP_ROUTES.INPATIENTS)}
            className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between"
          >
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-xl bg-[#FEF3C7] flex items-center justify-center text-[#F59E0B] flex-shrink-0">
                <FaProcedures className="w-5 h-5" />
              </div>
              <div className="flex items-end gap-1 h-6">
                <div className="w-1.5 h-2 bg-[#FDE68A] rounded-full" />
                <div className="w-1.5 h-3.5 bg-[#FCD34D] rounded-full" />
                <div className="w-1.5 h-5 bg-[#FBBF24] rounded-full" />
                <div className="w-1.5 h-6 bg-[#F59E0B] rounded-full" />
              </div>
            </div>
            <div className="my-2">
              <p className="text-xs font-bold text-gray-800">Wards</p>
              <p className="text-[11px] text-gray-400 font-medium">Inpatients</p>
              <p className="text-2xl font-extrabold text-[#041346] mt-0.5">28</p>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span>Critical</span>
                <span className="font-bold text-gray-800">4</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>Stable</span>
                <span className="font-bold text-gray-800">24</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Middle Section: 3 Grid Tables ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          {/* Table 1: Today's OPD Appointments */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Calendar className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-xs font-bold text-[#041346]">Today's OPD Appointments</h3>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/70 border-b border-gray-100 text-[11px] text-gray-400 font-semibold">
                      <th className="py-2.5 px-3">Patient</th>
                      <th className="py-2.5 px-2">Time</th>
                      <th className="py-2.5 px-2">Reason</th>
                      <th className="py-2.5 px-2">Status</th>
                      {/* <th className="py-2.5 px-3 text-center">Action</th> */}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-xs">
                    {OPD_APPOINTMENTS.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-6 h-6 rounded-full ${row.avatarBg} text-white font-bold text-[10px] flex items-center justify-center flex-shrink-0`}
                            >
                              {row.initial}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 text-[11px] truncate leading-tight">
                                {row.name}
                              </p>
                              <p className="text-[10px] text-gray-400 leading-tight">
                                {row.patientId}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-[11px] text-gray-600 font-medium whitespace-nowrap">
                          {row.time}
                        </td>
                        <td className="py-2.5 px-2 text-[11px] text-gray-600 truncate max-w-[90px]">
                          {row.reason}
                        </td>
                        <td className="py-2.5 px-2 whitespace-nowrap">
                          {getStatusBadge(row.status, row.statusType)}
                        </td>
                        {/* <td className="py-2.5 px-3 text-center">
                          <Dropdown menu={{ items: actionMenuItems(row) }} trigger={["click"]}>
                            <button className="text-gray-400 hover:text-gray-600 p-1">
                              <MoreVertical className="w-3.5 h-3.5" />
                            </button>
                          </Dropdown>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Table 2: Today's OT Schedule */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-purple-50 text-purple-600 flex items-center justify-center">
                    <Scissors className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-xs font-bold text-[#041346]">Today's OT Schedule</h3>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/70 border-b border-gray-100 text-[11px] text-gray-400 font-semibold">
                      <th className="py-2.5 px-3">Time</th>
                      <th className="py-2.5 px-2">Procedure</th>
                      <th className="py-2.5 px-2">OT Room</th>
                      <th className="py-2.5 px-2">Patient</th>
                      <th className="py-2.5 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-xs">
                    {OT_SCHEDULE.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-2.5 px-3 text-[11px] text-gray-600 font-medium whitespace-nowrap">
                          {row.time}
                        </td>
                        <td className="py-2.5 px-2 text-[11px] font-semibold text-gray-800 truncate max-w-[110px]">
                          {row.procedure}
                        </td>
                        <td className="py-2.5 px-2 text-[11px] text-gray-500 whitespace-nowrap">
                          {row.otRoom}
                        </td>
                        <td className="py-2.5 px-2 text-[11px] text-gray-700 truncate max-w-[80px]">
                          {row.patient}
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          {getStatusBadge(row.status, row.statusType)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Table 3: ICU Patients */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Bed className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-xs font-bold text-[#041346]">ICU Patients</h3>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/70 border-b border-gray-100 text-[11px] text-gray-400 font-semibold">
                      <th className="py-2.5 px-3">Patient</th>
                      <th className="py-2.5 px-2">Bed</th>
                      <th className="py-2.5 px-2">Condition</th>
                      <th className="py-2.5 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-xs">
                    {ICU_PATIENTS.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-2.5 px-3 text-[11px] font-semibold text-gray-800 whitespace-nowrap">
                          {row.patient}
                        </td>
                        <td className="py-2.5 px-2 text-[11px] text-gray-500 whitespace-nowrap">
                          {row.bed}
                        </td>
                        <td className="py-2.5 px-2 text-[11px] text-gray-600 truncate max-w-[100px]">
                          {row.condition}
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          {getStatusBadge(row.status, row.statusType)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Bottom Section: Emergency - Recent Arrivals ─── */}
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-red-50/70 border-b border-red-100/80 px-4 py-2.5 flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-red-100 text-red-600 flex items-center justify-center">
              <FaAmbulance className="w-3.5 h-3.5" />
            </div>
            <h3 className="text-xs font-bold text-red-700">Emergency - Recent Arrivals</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100 text-[11px] text-gray-400 font-semibold">
                  <th className="py-2.5 px-4">Patient</th>
                  <th className="py-2.5 px-4">Arrival Time</th>
                  <th className="py-2.5 px-4">Chief Complaint</th>
                  <th className="py-2.5 px-4">Priority</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-xs">
                {EMERGENCY_ARRIVALS.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-7 h-7 rounded-full ${row.avatarBg} text-white font-bold text-[11px] flex items-center justify-center flex-shrink-0`}
                        >
                          {row.initial}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 text-xs truncate leading-tight">
                            {row.name}
                          </p>
                          <p className="text-[10px] text-gray-400 leading-tight">
                            {row.patientId}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-gray-600 font-medium whitespace-nowrap">
                      {row.arrivalTime}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-gray-700 font-medium">
                      {row.chiefComplaint}
                    </td>
                    <td className="py-2.5 px-4 whitespace-nowrap">
                      {getStatusBadge(row.priority, row.priorityType)}
                    </td>
                    <td className="py-2.5 px-4 whitespace-nowrap">
                      {getStatusBadge(row.status, row.statusType)}
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      <Dropdown menu={{ items: actionMenuItems(row) }} trigger={["click"]}>
                        <button className="text-gray-400 hover:text-gray-600 bg-white p-1">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layouts>
  );
}

export default Analytics;
